<template>
    <transition-root
        :show="csvExport.hasOwnProperty('id')"
        enter="transition-opacity duration-150"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity duration-150"
        leave-from="opacity-100"
        leave-to="opacity-0"
    >
        <card class="fixed bottom-2 left-2 z-50">
            <div class="flex items-center justify-center dark:text-white">
                <svg
                    v-if="csvExport.status === 'processing' || csvExport.status === 'pending'"
                    class="-ml-1 mr-3 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle
                        class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4"
                    ></circle>
                    <path
                        class="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                </svg>

                <h1>Exportando {{ csvExport.name }}</h1>

                <span
                    :class="{
                        'bg-green-500': csvExport.status === 'completed',
                        'animate-pulse bg-yellow-500':
                            csvExport.status === 'processing' ||
                            csvExport.status === 'pending',
                        'bg-red-500': csvExport.status === 'failed',
                    }"
                    class="ml-2 rounded px-2 py-1 text-center text-white dark:text-gray-200"
                >
                    {{ statusName(csvExport.status) }}
                </span>
                <strong
                    class="ml-2"
                    v-if="csvExport.rows || csvExport.progress"
                >
                    {{ csvExport.progress || 0 }}/{{ csvExport.rows }}
                </strong>

                <a
                    v-if="csvExport.status === 'completed' && s3Link"
                    :href="s3Link"
                    target="_blank"
                    class="btn-primary-sm ml-3"
                >
                    Download
                    <cloud-arrow-down-icon
                        class="ml-1 h-5 w-5 text-gray-400 transition-all"
                    />
                </a>
                <x-mark-icon
                    @click="dismiss"
                    class="ml-1 h-5 w-5 text-gray-400 transition-all hover:cursor-pointer"
                />
            </div>
        </card>
    </transition-root>
</template>

<script>
import {
    XMarkIcon,
    CloudArrowDownIcon,
    ChevronDownIcon,
} from "@heroicons/vue/24/outline";
import Card from "../layouts/Card.vue";
import { TransitionRoot } from "@headlessui/vue";
import { mapState } from "pinia";
import { useExportStore } from "../../stores/exportStore";
import { useToast } from "vue-toastification";

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_MS = 15 * 60 * 1000;
const FINAL_STATUSES = ["completed", "failed"];

export default {
    components: {
        ChevronDownIcon,
        TransitionRoot,
        Card,
        XMarkIcon,
        CloudArrowDownIcon,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            csvExport: {},
            s3Link: false,
            pollTimer: null,
            startedAt: null,
            currentTrackingToken: null,
        };
    },
    computed: {
        ...mapState(useExportStore, ["activeExportId", "trackingToken"]),
    },
    watch: {
        trackingToken(token) {
            if (!this.activeExportId) {
                return;
            }

            this.beginPolling(this.activeExportId, token);
        },
    },
    beforeUnmount() {
        this.clearPolling();
    },
    methods: {
        beginPolling(exportId, token) {
            this.clearPolling();
            this.currentTrackingToken = token;
            this.startedAt = Date.now();
            this.csvExport = {
                id: exportId,
                name: "Statement",
                status: "pending",
                progress: 0,
                rows: 0,
            };
            this.s3Link = false;
            this.pollExport(exportId, token);
            this.pollTimer = setInterval(() => {
                this.pollExport(exportId, token);
            }, POLL_INTERVAL_MS);
        },
        async pollExport(exportId, token) {
            if (token !== this.currentTrackingToken) {
                return;
            }

            if (Date.now() - this.startedAt >= MAX_POLL_MS) {
                this.clearPolling();
                this.csvExport = {
                    ...this.csvExport,
                    status: "failed",
                };
                this.toast.error(
                    "A exportação está demorando mais que o esperado. Verifique seu e-mail em alguns minutos.",
                );
                useExportStore().stopTracking();
                return;
            }

            try {
                const { data } = await axios.get(`/api/exports/${exportId}`);
                if (token !== this.currentTrackingToken) {
                    return;
                }

                this.csvExport = {
                    id: data.id,
                    name: data.name,
                    status: data.status,
                    progress: data.progress,
                    rows: data.rows,
                };
                this.s3Link = data.download_url || false;

                if (FINAL_STATUSES.includes(data.status)) {
                    this.clearPolling();
                    useExportStore().stopTracking();

                    if (data.status === "failed") {
                        this.toast.error(
                            "Falha ao exportar. Tente novamente ou aguarde o e-mail.",
                        );
                    }
                }
            } catch (error) {
                if (error?.response?.status === 404) {
                    this.clearPolling();
                    this.csvExport = {
                        ...this.csvExport,
                        status: "failed",
                    };
                    useExportStore().stopTracking();
                    this.toast.error("Exportação não encontrada.");
                }
            }
        },
        clearPolling() {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },
        dismiss() {
            this.clearPolling();
            this.csvExport = {};
            this.s3Link = false;
            useExportStore().stopTracking();
        },
        statusName(status) {
            switch (status) {
                case "pending":
                    return "Pendente";
                case "processing":
                    return "Processando";
                case "completed":
                    return "Concluído";
                case "failed":
                    return "Falhou";
                default:
                    return status;
            }
        },
    },
};
</script>
